import React from 'react';
import { useLocale } from '../../i18n/locale';
import { appConfiguration } from '../../configuration/appConfiguration';

/**
 * Screenshot crop regions, mounted once at the top of the app. Each region is a
 * hardcoded viewport rectangle the screenshot spec clips to by `data-testid`.
 * Use `VITE_SHOW_SCREENSHOT_REGIONS=true` if you need visual aid to define regions.
 * Regions are grouped by locale, falling back to `default`.
 */

export type RegionRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  // used to show a region during development
  only?: boolean;
};

const SHOW_REGIONS = appConfiguration.showScreenshotRegions;

const DEFAULT_LOCALE_KEY = 'default';

/**
 * Regions grouped by page id, then by locale. `default` applies to any locale
 * without a specific entry. Add pages/regions here as needed.
 */
const REGIONS: Record<string, Record<string, RegionRect[]>> = {
  getStarted: {
    default: [
      {
        id: 'get-started-get-started-step-2',
        x: 30,
        y: 55,
        width: 1240,
        height: 315,
      },
      {
        id: 'get-started-overview-quick-actions',
        x: 25,
        y: 60,
        width: 860,
        height: 120,
      },
      {
        id: 'get-started-overview-your-insights',
        x: 25,
        y: 180,
        width: 1230,
        height: 200,
      },
      {
        id: 'get-started-overview-our-partners',
        x: 25,
        y: 375,
        width: 1230,
        height: 450,
      },
      {
        id: 'get-started-overview-resources',
        x: 25,
        y: 835,
        width: 1230,
        height: 220,
      },
    ],
  },
  cardManagement: {
    default: [
      {
        id: 'card-management-vehicle-overview-table-filters',
        x: 0,
        y: 0,
        width: 800,
        height: 500,
      },
      {
        id: 'card-management-vehicle-overview-services',
        x: 960,
        y: 260,
        width: 290,
        height: 245,
      },
      {
        id: 'card-management-vehicle-overview-vehicle-configuration',
        x: 885,
        y: 50,
        width: 390,
        height: 330,
      },
      {
        id: 'card-management-adding-cards-vehicle-configuration',
        x: 885,
        y: 50,
        width: 390,
        height: 330,
      },
      {
        id: 'card-management-adding-cards-failed-card',
        x: 20,
        y: 541,
        width: 1240,
        height: 70,
      },
      {
        id: 'card-management-activating-services-vehicle-configuration',
        x: 885,
        y: 50,
        width: 390,
        height: 330,
      },
      {
        id: 'card-management-activating-services-remove-service',
        x: 405,
        y: 190,
        width: 460,
        height: 365,
      },
    ],
  },
  transactionOverview: {
    default: [
      {
        id: 'transaction-overview-transactions-list-filters',
        x: 65,
        y: 65,
        width: 1240,
        height: 90,
      },
      {
        id: 'transaction-overview-transactions-list-statuses',
        x: 1300,
        y: 165,
        width: 665,
        height: 288,
      },
      {
        id: 'transaction-overview-transactions-list-prices',
        x: 1300,
        y: 165,
        width: 405,
        height: 432,
      },
      {
        id: 'transaction-overview-transactions-list-export',
        x: 1735,
        y: 85,
        width: 250,
        height: 70,
      },
    ],
  },
};

/** Resolves the regions for a given page + locale, falling back to `default`. */
export const regionsFor = (pageId: string, locale: string): RegionRect[] => {
  const page = REGIONS[pageId];
  if (!page) {
    return [];
  }
  return page[locale] ?? page[DEFAULT_LOCALE_KEY] ?? [];
};

/** Every region across every page, for the current locale. */
const allRegionsForLocale = (locale: string): RegionRect[] =>
  Object.keys(REGIONS).flatMap((pageId) => regionsFor(pageId, locale));

/**
 * Renders all crop regions for the active locale as absolutely-positioned,
 * normally-invisible boxes. Mounted once at the top of the app.
 */
export const ScreenshotRegions: React.FC = () => {
  const { locale } = useLocale();
  const regions = allRegionsForLocale(locale);

  if (regions.length === 0) {
    return null;
  }

  // If any region opts in with `only`, restrict the visible overlay to those.
  const hasOnly = regions.some((region) => region.only);

  return (
    <div
      data-testid="screenshot-regions-root"
      aria-hidden
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {regions.map((region) => {
        const visible = SHOW_REGIONS && (!hasOnly || Boolean(region.only));

        return (
          <div
            key={region.id}
            data-testid={region.id}
            data-screenshot-region="true"
            style={{
              position: 'fixed',
              left: region.x,
              top: region.y,
              width: region.width,
              height: region.height,
              boxSizing: 'border-box',
              pointerEvents: 'none',
              background: visible ? 'rgba(255, 0, 128, 0.12)' : 'transparent',
            }}
          >
            {visible && (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  padding: '1px 4px',
                  font: '11px/1.4 monospace',
                  color: '#fff',
                  background: 'rgba(255, 0, 128, 0.9)',
                  whiteSpace: 'nowrap',
                }}
              >
                {region.id}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
